import React, { useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import FeatureIcon from '../../components/FeatureIcon';
import { premiumGold } from '../../components/canonicalAccents';
import './EventLifecycle.css';

interface Stage {
  label: string;
  title: string;
  body: string;
  role: string;
  link: { to: string; label: string };
  branch?: boolean;
}

const STAGES: Stage[] = [
  {
    label: 'Create',
    title: 'Put the plan in place.',
    body: 'Give the event an idea, a meetup place and a time so riders know what you’re bringing together.',
    role: 'Host: publish the plan.',
    link: { to: '/help#create-event', label: 'Create an event' },
  },
  {
    label: 'Join',
    title: 'Choose the event that fits.',
    body: 'Read the plan and requirements, choose your motorcycle and RSVP. Joining shows interest, not completed attendance.',
    role: 'Rider: choose a bike and RSVP.',
    link: { to: '/help#join-event', label: 'Find and join an event' },
  },
  {
    label: 'Check in',
    title: 'Bring the group together at the meetup.',
    body: 'Confirm arrival with the check-in options available for the event.',
    role: 'Host and riders: confirm arrival. The host can manage riders’ check-ins.',
    link: { to: '/help#check-in', label: 'Check-in guide' },
  },
  {
    label: 'Take part',
    title: 'Get the event underway.',
    body: 'For a ride event, the checked-in host uses Start Event. Riders take part with the group.',
    role: 'Host: Start; riders: participate.',
    link: { to: '/help#host-chat', label: 'Event chat guide' },
    branch: true,
  },
  {
    label: 'Complete',
    title: 'Make it part of your history.',
    body: 'The completed event becomes part of the record of what you’ve taken part in.',
    role: 'Host: end the ride. Riders: look back on their participation.',
    link: { to: '/help#event-journey', label: 'Completion in the event journey' },
  },
  {
    label: 'Feedback',
    title: 'Learn from the people who were there.',
    body: 'Eligible attendees and the host can leave optional feedback after completion, when offered in the app.',
    role: 'Host and riders: share feedback based on recorded attendance.',
    link: { to: '/help#event-journey', label: 'Feedback in the event journey' },
  },
];

const EventLifecycleOverview: React.FC = () => {
  const { theme, dsColors, colors } = useTheme();
  const heroPrimary = colors.textPrimary;
  const heroSecondary = colors.textSecondary;
  const uid = useId();
  const [selected, setSelected] = useState(0);
  const railRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const bringIntoRail = (i: number) => {
    const rail = railRef.current;
    const tab = tabRefs.current[i];
    if (!rail || !tab) return;
    const pad = 8;
    if (tab.offsetLeft < rail.scrollLeft + pad) {
      rail.scrollLeft = Math.max(0, tab.offsetLeft - pad);
    } else if (tab.offsetLeft + tab.offsetWidth > rail.scrollLeft + rail.clientWidth - pad) {
      rail.scrollLeft = tab.offsetLeft + tab.offsetWidth - rail.clientWidth + pad;
    }
  };

  const select = (i: number, focus = false) => {
    const next = (i + STAGES.length) % STAGES.length;
    setSelected(next);
    if (focus) tabRefs.current[next]?.focus({ preventScroll: true });
    bringIntoRail(next);
  };

  const onTabKeyDown = (e: React.KeyboardEvent, i: number) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); select(i + 1, true); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); select(i - 1, true); }
    else if (e.key === 'Home') { e.preventDefault(); select(0, true); }
    else if (e.key === 'End') { e.preventDefault(); select(STAGES.length - 1, true); }
  };

  const stage = STAGES[selected];
  const gold = premiumGold(theme);

  return (
    <section className="py-12 md:py-16" style={{ backgroundColor: colors.surface }}>
      <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
        <div className="max-w-2xl mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: heroPrimary, textWrap: 'balance' }}>
            From the first plan to the final feedback.
          </h2>
          <p className="text-lg leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
            Make the plan, see who’s coming, meet up and take part. Afterward, the event becomes part of your history, with feedback from the people who were there.
          </p>
          <p className="text-sm mt-4" style={{ color: heroSecondary }}>
            A ride-event journey. Actions vary by event category and recorded attendance.
          </p>
        </div>
        <div
          className="lifejourney"
          style={{ '--lj-rail': colors.border, '--lj-surface': colors.surface, '--lj-accent': dsColors.primary } as React.CSSProperties}
        >
          <div
            className="lj-rail"
            ref={railRef}
            role="tablist"
            aria-label="Event journey stages"
            aria-orientation="horizontal"
          >
            {STAGES.map((s, i) => (
              <button
                key={s.label}
                ref={(el) => { tabRefs.current[i] = el; }}
                id={`${uid}-tab-${i}`}
                role="tab"
                type="button"
                className="lj-tab"
                aria-selected={selected === i}
                aria-controls={`${uid}-panel`}
                tabIndex={selected === i ? 0 : -1}
                onClick={() => select(i)}
                onKeyDown={(e) => onTabKeyDown(e, i)}
                onFocus={() => select(i)}
              >
                <span className="lj-tab-num" aria-hidden="true">{i + 1}</span>
                {s.label}
              </button>
            ))}
          </div>
          <div
            id={`${uid}-panel`}
            role="tabpanel"
            aria-labelledby={`${uid}-tab-${selected}`}
            tabIndex={0}
            className="lj-panel"
            style={{ color: heroSecondary }}
          >
            <h3 className="text-xl sm:text-2xl font-bold mb-3" style={{ color: heroPrimary, textWrap: 'balance' }}>
              {stage.title}
            </h3>
            <p className="text-base leading-relaxed max-w-2xl mb-3" style={{ textWrap: 'pretty' }}>{stage.body}</p>
            <p className="text-sm font-semibold mb-4" style={{ color: heroPrimary }}>{stage.role}</p>
            {stage.branch && (
              <p className="text-sm mb-4 leading-snug">
                <Link
                  to="/grouppulse"
                  className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
                  style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
                >
                  Optional GroupPulse →
                </Link>{' '}
                <span>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: gold }} aria-hidden="true">
                    <FeatureIcon name="crown" size={11} />
                  </span>{' '}
                  A Pro host enables it; riders choose how they share.
                </span>
              </p>
            )}
            <p>
              <Link
                to={stage.link.to}
                className="inline-flex items-center font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
                style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary, minHeight: '44px' } as React.CSSProperties}
              >
                {stage.link.label} →
              </Link>
            </p>
          </div>
        </div>
        <p className="mt-8">
          <Link
            to="/help#event-journey"
            className="inline-flex items-center font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded"
            style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary, minHeight: '44px' } as React.CSSProperties}
          >
            Follow the journey in Help →
          </Link>
        </p>
      </div>
    </section>
  );
};

export default EventLifecycleOverview;
